// @core/stores/tag/TagStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { tagApi } from '@core/api';
import type {
  BatchGetEntityTagsRequest,
  BatchGetEntityTagsResponse,
  BulkTagEntitiesRequest,
  BulkTagEntitiesResponse,
  BulkUntagEntitiesRequest,
  CreateTagRequest,
  ListTaggedEntitiesResponse,
  ListTagsResponse,
  Tag,
  TagEntityRequest,
  TagEntityResponse,
  UpdateTagRequest,
} from '@core/types/tag';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class TagStore {
  // List state
  items: Tag[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Tag | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Tag>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Tag | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Tag | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Batch get entity tags
   */
  async BatchGetEntityTags(data: BatchGetEntityTagsRequest): Promise<BatchGetEntityTagsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await tagApi.BatchGetEntityTags(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Bulk tag entities
   */
  async BulkTagEntities(data: BulkTagEntitiesRequest): Promise<BulkTagEntitiesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await tagApi.BulkTagEntities(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Bulk untag entities
   */
  async BulkUntagEntities(data: BulkUntagEntitiesRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await tagApi.BulkUntagEntities(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create tag
   */
  async CreateTag(data: CreateTagRequest): Promise<Tag> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await tagApi.CreateTag(data);
      const item = response.tag;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Delete tag
   */
  async DeleteTag(tag_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await tagApi.DeleteTag(tag_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== tag_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === tag_id) {
          this.currentItem = null;
        }

        this.removeFromCache(tag_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Get tag by ID
   */
  async GetTag(tag_id: string): Promise<Tag> {
    // Check cache first
    const cached = this.cache.get(tag_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await tagApi.GetTag(tag_id);
      const item = response.tag;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List tags for an entity
   */
  async ListEntityTags(entity_type: string, entity_id: string, params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await tagApi.ListEntityTags(entity_type, entity_id, params);

      runInAction(() => {
        this.items = response.tags?.tags ?? [];
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List entities with tag
   */
  async ListTaggedEntities(tag_id: string, params?: { cursor?: string | number | boolean; direction?: string | number | boolean; entity_type?: string | number | boolean; limit?: string | number | boolean; product_id?: string | number | boolean; sort?: string | number | boolean; }): Promise<ListTaggedEntitiesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await tagApi.ListTaggedEntities(tag_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List tags
   */
  async ListTags(params?: PaginatedRequest & { product_id?: string | number | boolean; q?: string | number | boolean; visibility?: string | number | boolean; }): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await tagApi.ListTags(params);

      runInAction(() => {
        this.items = response.tags?.tags ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Search tags
   */
  async SearchTags(params?: { cursor?: string | number | boolean; direction?: string | number | boolean; limit?: string | number | boolean; product_id?: string | number | boolean; q?: string | number | boolean; sort?: string | number | boolean; visibility?: string | number | boolean; }): Promise<ListTagsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await tagApi.SearchTags(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Tag an entity
   */
  async TagEntity(tag_id: string, data: TagEntityRequest): Promise<TagEntityResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await tagApi.TagEntity(tag_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Untag an entity
   */
  async UntagEntity(tag_id: string, entity_type: string, entity_id: string): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await tagApi.UntagEntity(tag_id, entity_type, entity_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update tag
   */
  async UpdateTag(tag_id: string, data: UpdateTagRequest): Promise<Tag> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await tagApi.UpdateTag(tag_id, data);
      const item = response.tag;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const tagStore = new TagStore();
